import React, {useEffect, useRef, useState} from 'react';
import Dialog from "@material-ui/core/Dialog";
import HCLTreeMxGraphView, {HCLTreeMxGraphViewJSON} from "../Model/SingleHCLModelView/SingleTreeView/MxGraphViews/HCLTreeMxGraphView";
import {FaultTreeMxGraphViewJSON} from "../Model/SingleHCLModelView/SingleTreeView/MxGraphViews/FaultTreeMxGraphEditorView";
import {EventTreeMxGraphViewJSON} from "../Model/SingleHCLModelView/SingleTreeView/MxGraphViews/EventTreeMxGraphEditorView";
import {BayesianNetworkMxGraphViewJSON} from "../Model/SingleHCLModelView/SingleTreeView/MxGraphViews/BayesianNetworkMxGraphEditorView";
import {makeStyles} from "@material-ui/core/styles";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import {withRouter} from 'react-router-dom';
import {mxCell} from 'mxgraph-js';
import HclApiManager from "../../Api/HclApiManager";
import CircularIndeterminate
	from 'hcla-web-frontend-primitives/app/components/primitives/Progress/CircularIndeterminate';
import {Grid} from "@material-ui/core";
import ProbabilityInput from "../Model/SingleHCLModelView/SingleTreeView/Inputs/ProbabilityInput";
import Typography from "@material-ui/core/Typography";
import {Expression} from '../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Expression';
import ExpressionInput from "../Model/SingleHCLModelView/SingleTreeView/Inputs/ExpressionInput";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import TabPanel from "hcla-web-frontend-primitives/app/Utils/TabPanel";
import AppBar from "@material-ui/core/AppBar";
import ProxyTypes, {
	DistributionDictionary,
	DistributionTimeDependence
} from "../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ProxyTypes";
import InputRules from "../Model/SingleHCLModelView/SingleTreeView/Inputs/InputRules";

const useStyles = makeStyles(theme => ({
	paperScrollPaper: {
		overflowY: "auto"
	},
	graphContainer: {
		backgroundColor: "rgba(0, 0, 0, 0.05)",
		height: "45vh",
		overflow: 'auto',
		border: '1px solid #999',
		borderRadius: "12px",
		position: 'relative',
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		marginTop: "8px"
	},
	globalError: {
		padding: '12px',
		backgroundColor: '#F44336',
		color: 'white',
		marginBottom: theme.spacing(1)
	}
}));

interface Props {
	selectedCells: mxCell[];
	save: (e: any, json: FaultTreeMxGraphViewJSON | EventTreeMxGraphViewJSON | BayesianNetworkMxGraphViewJSON) => void;
	close: () => void;
	match: any;
}
const logNormalInit = {
	_proxy: ProxyTypes.LOG_NORMAL_DISTRIBUTION,
	distribution_time_dependence: DistributionTimeDependence.TIME_INDEPENDENT,
	log_normal_params: "mean & std"
};

const CommonCauseDialog = (props: Props) => {
	const { selectedCells, save, close, match, match: { path }} = props;
	const [data, setData] = useState(null);
	const [betaFactor, setBetaFactor] = useState<any>(null);
	const [alphaFactors, setAlphaFactors] = useState<any>(null);
	const [isDistribution, setIsDistribution] = useState<boolean>(false);
	const [globalError, setGlobalError] = useState<any>(null);
	const [tab, setTab] = useState(0);
	const [expressionError, setExpressionError] = useState(false);

	const graphRef = useRef(null);

	let basePath;
	switch(path) {
		case (path.includes("project")):
			basePath = "/project"
			break;
		case (path.includes("subsystem")):
			basePath = "/subsystem"
			break;
		case (path.includes("component")):
			basePath = "/component"
			break;
		default:
			basePath = "/model"
			break;
	}

	useEffect(() => {
		HclApiManager.getHclTreeWithMetadata(Number(match.params.treeId))
			.then((response: any) => response.ok && response.json())
			.then((data: HCLTreeMxGraphViewJSON) => setData(data));

		const isDistribution = !selectedCells[0].value.expression.getValue();
		if (selectedCells.length > 2) {
			setAlphaFactors(selectedCells.map((cell, i) => {
				return {
					cell: {...cell},
					id: i + 1,
					value: isDistribution ? logNormalInit : null,
				}
			}))
		}
		if (selectedCells.length === 2) {
			setBetaFactor({
				cell: {...selectedCells[0]},
				id: 1,
				value: isDistribution ? logNormalInit : null,
			})
		}
		if (isDistribution) {
			setIsDistribution(true);
		}
	},[]);
	const classes = useStyles();

	useEffect(() => {
		// Toggle alpha factor summation global error
		if (!isDistribution && alphaFactors && alphaFactors.reduce((a,{value}) => a + parseFloat(value), 0) !== 1) {
			setGlobalError("Sum of Alpha Factors' must be equal to 1")
		} else if (isDistribution && alphaFactors && alphaFactors.every(factor => factor.value.log_normal_mean) && alphaFactors.reduce((a,{value}) => a + parseFloat(value.log_normal_mean), 0) !== 1) {
			setGlobalError("Sum of Alpha Factors' `mean` must be equal to 1");
		} else {
			setGlobalError(null)
		}
	},[alphaFactors]);

	const shouldBeDisabled = () => {
		if (expressionError || globalError) {
			return true;
		}
		if (alphaFactors) {
			if (!isDistribution) {
				return alphaFactors.some((factor) => !factor.value);
			} else {
				return alphaFactors.some((factor) => !factor.value.log_normal_mean || !factor.value.log_normal_std)
			}
		} else {
			return betaFactor && !betaFactor.value;
		}
	}

	const handleAlphaFactorChange = (id: number) => (event) => {
		setAlphaFactors(alphaFactors.map((alphaFactor) => {
			if (alphaFactor.id !== id) return alphaFactor;
			return {
				id,
				value: event.target.value
			}
		}))
	}

	const handleBetafactorChange = (e) => {
		setBetaFactor(e.target.value);
		setBetaFactor({
			id: 1,
			value: e.target.value
		})
	}

	const handleChange = (id: number) => (e:any, name: string, multiple: boolean) => {
		e.persist();
		setAlphaFactors(alphaFactors.map((alphaFactor) => {
			if (alphaFactor.id !== id) return alphaFactor;
			return {
				id,
				value: multiple ? {...alphaFactor.value, ...e.target.value } : { ...alphaFactor.value, [e.target.name]: e.target.value }
			}
		}))
	}

	const handleChangeBeta = (e:any, name: string, multiple: boolean) => {
		e.persist();
		setBetaFactor({
			...betaFactor,
			value: multiple ? {...betaFactor.value, ...e.target.value } : { ...betaFactor.value, [e.target.name]: e.target.value }
		})
	}

	const saveGraph = () => {
		save(undefined, graphRef.current);
	}

	const renderAlphaFactors = () => {
		if (isDistribution) {
			return (
				<React.Fragment>
					<AppBar position="static" color="default">
						<Tabs
							value={tab}
							onChange={(event, newValue) => setTab(newValue)}
							indicatorColor="primary"
							textColor="primary"
							variant="scrollable"
							scrollButtons="auto"
						>
							{
								alphaFactors.map((alpha) => <Tab label={`α${alpha.id} factor`} style={{ fontSize: '1.2rem', textTransform: 'none' }} />)
							}
						</Tabs>
					</AppBar>
					{
						alphaFactors.map((alpha, index) => (
							<TabPanel value={tab} index={index}>
								<ExpressionInput
									expression={new Expression(alpha.value)}
									onChange={handleChange(alpha.id)}
									onError={(e) => setExpressionError(e)}
									name="expression"
									override={{
										type: ProxyTypes.DISTRIBUTION,
										eventType: DistributionTimeDependence.TIME_INDEPENDENT,
										distributionType: DistributionDictionary[DistributionTimeDependence.TIME_INDEPENDENT][ProxyTypes.LOG_NORMAL_DISTRIBUTION],
										params: ["mean", "std"],
									}}
								/>
							</TabPanel>
						))
					}
				</React.Fragment>
			)
		} else {
			return (
				alphaFactors.map((alpha) => (
					<Grid item xs={3}>
						<ProbabilityInput
							value={alpha.value}
							onChange={handleAlphaFactorChange(alpha.id)}
							label={`α${alpha.id} factor`}
							variant={"outlined"}
						/>
					</Grid>
				))
			)
		}
	}

	const renderBetaFactor = () => {
		if (isDistribution) {
			return (
				<React.Fragment>
					<ExpressionInput
						expression={new Expression(betaFactor.value)}
						onChange={handleChangeBeta}
						onError={(e) => setExpressionError(e)}
						name="expression"
						override={{
							type: ProxyTypes.DISTRIBUTION,
							eventType: DistributionTimeDependence.TIME_INDEPENDENT,
							distributionType: DistributionDictionary[DistributionTimeDependence.TIME_INDEPENDENT][ProxyTypes.LOG_NORMAL_DISTRIBUTION],
							params: ["mean", "std"],
						}}
					/>
				</React.Fragment>
			)
		} else {
			return (
				<ProbabilityInput
					value={betaFactor.value}
					onChange={handleBetafactorChange}
					label={`β factor`}
					variant={"outlined"}
				/>
			)
		}
	}

	const content = selectedCells && data
		? (
			<HCLTreeMxGraphView
				ref={(el: any) => graphRef.current = el && el.graph.toJSON()}
				data={data}
				basePath={basePath}
				commonCause={selectedCells}
				commonCauseFactors={alphaFactors ? alphaFactors : betaFactor}
			/>
		)
		: (
			<CircularIndeterminate />
		)
	return (
		<Dialog
			open={true}
			fullWidth
			maxWidth={false}
			classes={{ paperScrollPaper: classes.paperScrollPaper }}
		>
			<DialogTitle>
				Set Common Cause
			</DialogTitle>
			<div style={{ padding: '16px'}}>
				<Grid container>
					{
						globalError && (
							<Grid item xs={12} className={classes.globalError}>
								<Typography>{globalError}</Typography>
							</Grid>
						)
					}
					{
						alphaFactors
							? renderAlphaFactors()
							: betaFactor
							? renderBetaFactor()
							: null
					}
				</Grid>
				<div className={classes.graphContainer}>
					{content}
				</div>
			</div>
			<DialogActions>
				<Button color="primary" onClick={close}>
					Cancel
				</Button>
				<Button
					color="primary"
					variant={"contained"}
					autoFocus
					onClick={saveGraph}
					disabled={shouldBeDisabled()}
				>
					Apply
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default withRouter(CommonCauseDialog);